import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, iif, Observable, of } from 'rxjs';
import { filter, finalize, map, switchMap, take } from 'rxjs/operators';
import { ExchangeRate } from '../../../model/src/lib/exchange-rate.model';
import { AppConfig } from '../../config/app.config';
import { ApiBaseService } from './api-base.service';

type Request = {
  baseCurrencyCd: string;
  exchangeCurrencyCd: string;
  quotedDate: string;
};

@Injectable({
  providedIn: 'root',
})
export class ApiAccountingCommonService extends ApiBaseService {
  constructor(protected appConfig: AppConfig, protected http: HttpClient) {
    super(appConfig.getApiUrl('accountingCommon'), http);
  }

  cacheTransferLog: { amount: number; transferAmount: number }[] = [];
  private cacheRate: { [key: string]: ExchangeRate[] } = {};
  private lastCompleted$: BehaviorSubject<boolean>[] = [];

  /**
   * @param fromCurrency 幣別A
   * @param toCurrency 幣別B
   * @param middleRate 中間匯率
   */
  initRate(fromCurrency: string, toCurrency: string, middleRate: number): void {
    const key = `${fromCurrency}-${toCurrency}`;
    this.cacheRate[key] = [{ middleRate } as ExchangeRate];
  }

  /**
   * 取得對應幣別匯率
   * @param fromCurrency 幣別A
   * @param toCurrency 幣別B
   * @returns 中間匯率
   */
  getRate(fromCurrency: string, toCurrency: string): number {
    const key = `${fromCurrency}-${toCurrency}`;
    if (this.cacheRate[key]) {
      return this.cacheRate[key][0].middleRate;
    } else {
      if (fromCurrency === toCurrency) {
        return 1;
      } else {
        throw new Error('Not exist rate');
      }
    }
  }

  getExchangeRate(req: Request): Observable<{ exchangeRate: ExchangeRate[] }> {
    if (this.lastCompleted$.length === 0) {
      this.lastCompleted$.push(new BehaviorSubject<boolean>(true));
    } else {
      this.lastCompleted$.push(new BehaviorSubject<boolean>(false));
    }
    const key = `${req.baseCurrencyCd}-${req.exchangeCurrencyCd}`;
    return this.lastCompleted$[this.lastCompleted$.length - 1].pipe(
      filter((completed) => completed),
      switchMap(() => {
        return iif(
          () => this.cacheRate[key] !== undefined,
          of({ exchangeRate: this.cacheRate[key] }),
          this.doPost<{ exchangeRate: ExchangeRate[] }>(
            '/AccountingCommon/ExchangeRate',
            { filters: req }
          ).pipe(
            map((res) => {
              if (res.exchangeRate) {
                this.cacheRate[key] = res.exchangeRate;
              }
              return res;
            })
          )
        );
      }),
      take(1),
      finalize(() => {
        this.completed();
      })
    );
  }

  private completed(): void {
    const complete$ = this.lastCompleted$.shift();
    if (complete$ && complete$.getValue() === true) {
      const next$ = this.lastCompleted$.shift();
      next$?.next(true);
    } else {
      complete$?.next(true);
    }
  }
}
